import type { SelectChangeEvent } from '@mui/material';
import { Box, Skeleton, Typography } from '@mui/material';
import type { FC } from 'react';
import { Suspense, useCallback } from 'react';
import type { Option } from 'types/shared';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import { Cancel } from '@mui/icons-material';
import { CommonSelect } from 'components/common';
import { useListParams } from 'hooks';

type StatusesParam = 'failed' | 'success';
type FilterStatusOptions = Option<StatusesParam>[];

const STATUSES_LIST: FilterStatusOptions = [
  { value: 'failed', label: 'Failed' },
  { value: 'success', label: 'Success' },
];

const FilterStatusComponent = () => {
  const { setStatus } = useListParams();

  const handleChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      setStatus(e.target.value as string);
    },
    [setStatus]
  );

  return (
    <CommonSelect
      name={'status'}
      inputLabel={'Status'}
      options={STATUSES_LIST}
      placeholder={'Value'}
      handleChange={handleChange}
      clearable
    />
  );
};

const FilterStatusErrorBoundaryFallback: FC = () => {
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        gap: 1 / 2,
        px: 1,
        width: '100%',
        height: 40,
        alignItems: 'center',
        borderRadius: '12px',
        border: `1px solid ${t.palette.error.main}`,
      })}
    >
      <Cancel color={'error'} />
      <Typography variant={'body2'} color={'error'}>
        An unexpected error happened.
      </Typography>
    </Box>
  );
};

const FilterStatus: FC = () => {
  return (
    <Box sx={{ display: 'flex', minWidth: 108, flex: 1 }}>
      <GlobalErrorBoundary
        FallbackComponent={FilterStatusErrorBoundaryFallback}
      >
        <Suspense
          fallback={
            <Skeleton
              width={'100%'}
              height={'40px'}
              sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
            />
          }
        >
          <FilterStatusComponent />
        </Suspense>
      </GlobalErrorBoundary>
    </Box>
  );
};

export default FilterStatus;

